import React, { useCallback, useState, useEffect } from 'react';
import { Upload, Image as ImageIcon, AlertCircle, Loader2, X, Plus, ArrowRight } from 'lucide-react';
import { ACCEPTED_IMAGE_TYPES, MAX_FILE_SIZE_MB } from '../constants';
import { convertPdfToImage, isPdfFile } from '../utils/pdfToImage';

interface UploadZoneProps {
  onImagesSelected: (files: File[]) => void;
}

interface FilePreview {
  file: File;
  previewUrl: string;
}

const MAX_FILES = 10;

const UploadZone: React.FC<UploadZoneProps> = ({ onImagesSelected }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [convertingCount, setConvertingCount] = useState(0);
  const [filePreviews, setFilePreviews] = useState<FilePreview[]>([]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      filePreviews.forEach(fp => URL.revokeObjectURL(fp.previewUrl));
    };
  }, []);

  const validateFile = (file: File): boolean => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setError("Please upload JPG, PNG, WebP images, or PDF files.");
      return false;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(`Each file must be under ${MAX_FILE_SIZE_MB}MB.`);
      return false;
    }
    return true;
  };

  // Process files - convert PDFs to images
  const processFiles = async (files: File[]) => {
    setError(null);
    const validFiles = files.filter(validateFile);
    
    if (validFiles.length === 0) return;
    
    // Check max files limit
    if (filePreviews.length + validFiles.length > MAX_FILES) {
      setError(`Maximum ${MAX_FILES} files allowed.`);
      return;
    }
    
    const pdfFiles = validFiles.filter(isPdfFile);
    const imageFiles = validFiles.filter(f => !isPdfFile(f));
    
    // Process non-PDF files immediately
    const newPreviews: FilePreview[] = imageFiles.map(file => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    
    // Convert PDFs
    if (pdfFiles.length > 0) {
      setIsConverting(true);
      setConvertingCount(pdfFiles.length);
      
      for (const pdfFile of pdfFiles) {
        try {
          const imageFile = await convertPdfToImage(pdfFile);
          newPreviews.push({
            file: imageFile,
            previewUrl: URL.createObjectURL(imageFile),
          });
        } catch (err) {
          console.error('PDF conversion failed:', err);
          setError(`Failed to convert ${pdfFile.name}. Skipped.`);
        }
        setConvertingCount(prev => prev - 1);
      }
      
      setIsConverting(false);
    }
    
    setFilePreviews(prev => [...prev, ...newPreviews]);
  };

  const removeFile = (index: number) => {
    setFilePreviews(prev => {
      const removed = prev[index];
      URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
    setError(null);
  };

  const handleContinue = () => {
    if (filePreviews.length > 0) {
      onImagesSelected(filePreviews.map(fp => fp.file));
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setError(null);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      processFiles(files);
    }
  }, [filePreviews]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      processFiles(files);
    }
    // Reset input so the same file can be selected again
    e.target.value = '';
  };

  // Show converting state overlay
  const renderConvertingOverlay = () => {
    if (!isConverting) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
        <div className="text-center space-y-4">
          <div className="relative mx-auto w-fit">
            <div className="absolute inset-0 bg-teal-500/20 blur-xl rounded-full animate-pulse"></div>
            <Loader2 className="w-12 h-12 text-teal-500 animate-spin relative z-10" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-100">Converting PDF{convertingCount > 1 ? 's' : ''}</h3>
            <p className="text-slate-400 text-sm">
              {convertingCount} remaining...
            </p>
          </div>
        </div>
      </div>
    );
  };

  // If we have files, show preview grid with option to add more
  if (filePreviews.length > 0) {
    return (
      <div className="flex flex-col h-full w-full p-4 sm:p-6 animate-fade-in bg-black/50 backdrop-blur-sm">
        {renderConvertingOverlay()}
        
        <div className="max-w-3xl w-full mx-auto flex-1 flex flex-col">
          <div className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-100">Your Sketches</h2>
            <p className="text-slate-400 text-sm mt-1">
              {filePreviews.length} file{filePreviews.length !== 1 ? 's' : ''} ready • Add more or continue
            </p>
          </div>

          {/* File Grid */}
          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {filePreviews.map((fp, index) => (
                <div 
                  key={index} 
                  className="relative aspect-square rounded-xl overflow-hidden border border-slate-700 bg-slate-900 group"
                >
                  <img 
                    src={fp.previewUrl} 
                    alt={fp.file.name}
                    className="w-full h-full object-contain bg-white"
                  />
                  <button
                    onClick={() => removeFile(index)}
                    className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
                  >
                    <X size={14} />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                    <p className="text-xs text-white truncate">{fp.file.name}</p>
                  </div>
                </div>
              ))}

              {/* Add More Button */}
              {filePreviews.length < MAX_FILES && (
                <label className="aspect-square rounded-xl border-2 border-dashed border-slate-700 bg-slate-900/50 flex flex-col items-center justify-center cursor-pointer hover:border-teal-500/50 hover:bg-teal-900/10 transition-all">
                  <input
                    type="file"
                    accept={ACCEPTED_IMAGE_TYPES.join(',')}
                    multiple
                    className="hidden"
                    onChange={handleFileInput}
                  />
                  <Plus size={24} className="text-slate-500 mb-1" />
                  <span className="text-xs text-slate-500">Add More</span>
                </label>
              )}
            </div>
          </div>

          {error && (
            <div className="flex items-center justify-center gap-2 text-red-400 bg-red-900/20 border border-red-900/50 p-3 rounded-lg text-sm font-medium mt-4">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* Continue Button */}
          <div className="mt-6 pt-4 border-t border-slate-800">
            <button
              onClick={handleContinue}
              className="w-full py-4 rounded-2xl font-semibold text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 shadow-lg shadow-teal-900/30 hover:shadow-teal-900/50 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
            >
              Continue with {filePreviews.length} Sketch{filePreviews.length !== 1 ? 'es' : ''}
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Initial upload state
  return (
    <div className="flex flex-col items-center justify-center h-full w-full p-4 sm:p-6 animate-fade-in">
      {renderConvertingOverlay()}
      
      <div className="max-w-xl w-full text-center space-y-4 sm:space-y-8">
        <div className="space-y-2 sm:space-y-3">
          <h2 className="text-2xl sm:text-4xl font-bold text-slate-100">You Sketch, We Plan</h2>
          <p className="text-slate-400 text-sm sm:text-lg px-2">
            Upload your hand-drawn sketches and we'll transform them into a professional floor plan within 24 hours.
          </p>
        </div>

        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            relative border-2 border-dashed rounded-3xl sm:rounded-[2rem] p-8 sm:p-12 transition-all duration-300 ease-in-out
            flex flex-col items-center justify-center gap-3 sm:gap-4 cursor-pointer group
            ${isDragging 
              ? 'border-teal-500 bg-teal-900/20 scale-[1.02] shadow-xl shadow-teal-900/10' 
              : 'border-slate-700 bg-slate-900 hover:border-slate-600 hover:shadow-lg hover:shadow-slate-900/50'
            }
          `}
        >
          <input
            type="file"
            accept={ACCEPTED_IMAGE_TYPES.join(',')}
            multiple
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
            onChange={handleFileInput}
          />
          
          <div className={`
            w-16 h-16 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl flex items-center justify-center transition-colors
            ${isDragging ? 'bg-teal-900/40 text-teal-400' : 'bg-slate-800 text-slate-500 group-hover:bg-slate-700 group-hover:text-teal-400'}
          `}>
            {isDragging ? <Upload size={32} className="sm:w-10 sm:h-10" /> : <ImageIcon size={32} className="sm:w-10 sm:h-10" />}
          </div>

          <div className="space-y-1">
            <p className="font-medium text-base sm:text-lg text-slate-200">
              {isDragging ? 'Drop files here' : 'Click or drag files here'}
            </p>
            <p className="text-xs sm:text-sm text-slate-500">
              JPG, PNG, WebP, PDF up to {MAX_FILE_SIZE_MB}MB each • Multiple files supported
            </p>
          </div>
        </div>

        {error && (
          <div className="flex items-center justify-center gap-2 text-red-400 bg-red-900/20 border border-red-900/50 p-3 rounded-lg text-sm font-medium animate-shake">
            <AlertCircle size={16} />
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadZone;